import Icon from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import { CUSTOMER_MERGE_RULE } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import {
  formatMismatchCustomerName,
  getMismatchSideTarget,
  type MismatchSide,
} from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.utils"
import type { UserCustomerMismatchFragment } from "@/v2-backoffice-client/user/customerMismatch/__types__/user.customerMismatch.fragment"

export type UserCustomerMismatchCustomerCellProps = {
  mismatch: UserCustomerMismatchFragment
  side: MismatchSide
}

/**
 * One side's customer on the board. A customer HubSpot still has is just
 * its name. One whose company HubSpot merged away is struck through, with
 * the customer it was merged into after the arrow: "this one is gone, it
 * is that one now", which is also where the row's buttons move things.
 * The tooltip carries the ids and the rule.
 */
function UserCustomerMismatchCustomerCell(
  props: UserCustomerMismatchCustomerCellProps,
) {
  const { mismatch, side } = props
  const name =
    side === "user"
      ? formatMismatchCustomerName(
          mismatch.UserCustomerName,
          mismatch.UserCustomerExternalId,
        )
      : formatMismatchCustomerName(
          mismatch.TeamCustomerName,
          mismatch.TeamCustomerExternalId,
        )
  const mergedInto =
    side === "user"
      ? mismatch.UserCustomerSurvivingExternalCompanyId
      : mismatch.TeamCustomerSurvivingExternalCompanyId
  if (!mergedInto) return name

  const target = getMismatchSideTarget(mismatch, side)
  const survivorLabel = target?.Name ?? `company ${mergedInto}`
  const explanation = target
    ? `HubSpot merged this customer's company into ${target.Name} (${mergedInto}). ${side === "user" ? "The user" : "The team"} belongs on ${target.Name}.`
    : `HubSpot merged this customer's company into company ${mergedInto}, which has no customer yet; the sync links one within the hour.`

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span className="inline-flex max-w-full items-center gap-1.5">
          <s className="truncate text-muted-foreground">{name}</s>
          <Icon
            src="arrow-right"
            size={10}
            className="shrink-0 text-destructive"
            aria-label="merged into"
          />
          <span
            className={target ? "truncate" : "truncate text-muted-foreground"}
          >
            {survivorLabel}
          </span>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-2 p-3">
          <p className="font-medium text-popover-foreground">
            Merged away in HubSpot
          </p>
          <p className="text-muted-foreground">{explanation}</p>
          <p className="text-xs text-muted-foreground">{CUSTOMER_MERGE_RULE}</p>
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default UserCustomerMismatchCustomerCell
